import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import SEOHead from '@/components/feature/SEOHead';
import SchemaOrg from '@/components/feature/SchemaOrg';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { categories, articles, type CategoryInfo, type ArticleItem } from '@/mocks/categories';

const categorySlugs: { slug: string; info: CategoryInfo }[] = Object.entries(categories).map(([slug, info]) => ({
  slug,
  info,
}));

export default function CategoryPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeSlug = searchParams.get('tab') || 'business';
  const [activeCategory, setActiveCategory] = useState(activeSlug);

  useEffect(() => {
    setActiveCategory(activeSlug);
  }, [activeSlug]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' as const });
  }, [activeSlug]);

  const handleCategoryChange = (slug: string) => {
    setSearchParams({ tab: slug });
  };

  const filteredArticles: ArticleItem[] = articles.filter((a) => a.category === activeCategory);
  const currentCat = categories[activeCategory];

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: '分类浏览' },
  ];

  return (
    <>
      <SEOHead
        title={`分类浏览 - 17.c-起草口 | 专业文案起草分类入口`}
        description="17.c-起草口 提供商务文件、创意文案、法律文书、行政公文、学术论文、个人文书六大分类的起草指南，覆盖全场景起草需求。按分类浏览，快速找到您需要的起草指导。"
        keywords="起草分类,文案分类,商务文件起草,创意文案,法律文书,行政公文,学术论文,17.c-起草口"
        canonical="/category"
      />

      <SchemaOrg
        data={{
          '@context': 'https://schema.org',
          '@type': 'CollectionPage',
          name: '起草分类浏览 - 17.c-起草口',
          description: '按分类浏览所有起草指南',
          url: 'https://zhimycloud.cn/category',
        }}
      />

      <SchemaOrg
        data={{
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: '首页', item: 'https://zhimycloud.cn/' },
            { '@type': 'ListItem', position: 2, name: '分类浏览', item: 'https://zhimycloud.cn/category' },
          ],
        }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <div className="pt-20 pb-6 bg-background-100">
          <div className="max-w-6xl mx-auto px-4 md:px-6">
            <Breadcrumb items={breadcrumbItems} />
          </div>
        </div>

        <div className="bg-background-100 pb-10">
          <div className="max-w-6xl mx-auto px-4 md:px-6">
            <div className="mb-8">
              <h1 className="text-2xl md:text-4xl font-bold text-foreground-950 mb-3">
                分类浏览
              </h1>
              <p className="text-foreground-500 text-base">
                按分类浏览起草指南，快速找到您需要的专业内容
              </p>
            </div>

            {/* Category Tabs */}
            <div className="flex flex-wrap gap-2 mb-10">
              {categorySlugs.map(({ slug, info }) => (
                <button
                  key={slug}
                  onClick={() => handleCategoryChange(slug)}
                  className={`px-5 py-2.5 rounded-full text-sm font-medium transition-colors duration-200 whitespace-nowrap ${
                    activeCategory === slug
                      ? 'bg-accent-500 text-background-50'
                      : 'bg-background-50 text-foreground-600 hover:bg-background-200/70'
                  }`}
                >
                  <i className={`${info.icon} mr-1.5`}></i>
                  {info.name}
                </button>
              ))}
            </div>

            {/* Category Info */}
            {currentCat && (
              <div className="bg-background-50 rounded-lg p-6 mb-8">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-accent-100 rounded-lg flex items-center justify-center shrink-0">
                    <i className={`${currentCat.icon} text-accent-600 text-xl`}></i>
                  </div>
                  <div>
                    <h2 className="text-foreground-950 font-semibold text-lg mb-1.5">
                      {currentCat.name}
                    </h2>
                    <p className="text-foreground-600 text-sm leading-relaxed">
                      {currentCat.description}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Articles Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {filteredArticles.map((article) => (
                <Link
                  key={article.id}
                  to={`/article/${article.slug}`}
                  className="bg-background-50 rounded-lg p-6 hover:bg-background-100/80 transition-all duration-200 group flex flex-col h-full"
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-3 flex-wrap">
                      {article.tags.slice(0, 2).map((tag) => (
                        <span key={tag} className="px-2.5 py-1 bg-accent-100 text-accent-700 rounded-full text-xs font-medium whitespace-nowrap">
                          {tag}
                        </span>
                      ))}
                    </div>
                    <h3 className="text-foreground-950 font-semibold text-base mb-2 group-hover:text-accent-600 transition-colors line-clamp-2">
                      {article.title}
                    </h3>
                    <p className="text-foreground-600 text-sm leading-relaxed line-clamp-3 mb-4">
                      {article.excerpt}
                    </p>
                  </div>
                  <div className="flex items-center justify-between text-foreground-400 text-xs pt-4 border-t border-background-200">
                    <div className="flex items-center gap-1.5">
                      <i className="ri-user-line"></i>
                      <span>{article.author}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="flex items-center gap-1">
                        <i className="ri-time-line"></i>
                        {article.readTime} 分钟
                      </span>
                      <span>{article.publishDate}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            {filteredArticles.length === 0 && (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto bg-background-100 rounded-full flex items-center justify-center mb-4">
                  <i className="ri-inbox-line text-foreground-400 text-2xl"></i>
                </div>
                <p className="text-foreground-500 text-sm">该分类下暂无文章</p>
              </div>
            )}
          </div>
        </div>

        <Footer />
      </div>
    </>
  );
}